import axios from "../../services/axios";
const BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * User API Key Management
 * Endpoints for users to manage their own API keys
 */

export interface CreateApiKeyRequest {
  name: string;
  expires_in_days?: number; // Optional, 1-365 days
}

export interface ApiKeyResponse {
  id: number;
  name: string;
  api_key: string; // Only shown once!
  key_prefix: string;
  is_active: boolean;
  created_at: string;
  expires_at: string | null;
}

export interface ApiKey {
  id: number;
  name: string;
  key_prefix: string;
  is_active: boolean;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
}

export interface ListApiKeysResponse {
  api_keys: ApiKey[];
}

export interface RevokeApiKeyResponse {
  message: string;
}

/**
 * Create a new API key for the authenticated user
 * POST /me/api_keys
 */
export const createUserApiKey = async (
  data: CreateApiKeyRequest
): Promise<ApiKeyResponse | any> => {
  try {
    const url = `${BASE_URL}/me/api_keys`;
    const response = await axios.post(url, data);
    return response.data;
  } catch (error: any) {
    console.log("Error creating user API key:", error);
    if (error.code === "ERR_CANCELED") return { canceled: true };

    return {
      error: {
        message: error?.response?.data?.details ?? "Failed to create API key.",
        error: error,
        status: error?.response?.status ?? 504,
      },
    };
  }
};

/**
 * List all API keys for the authenticated user
 * GET /me/api_keys
 */
export const getUserApiKeys = async (): Promise<ApiKey[] | any> => {
  try {
    const url = `${BASE_URL}/me/api_keys`;
    const response = await axios.get<ListApiKeysResponse>(url);
    return response.data.api_keys;
  } catch (error: any) {
    console.log("Error fetching user API keys:", error);
    if (error.code === "ERR_CANCELED") return { canceled: true };

    return {
      error: {
        message: error?.response?.data?.details ?? "Failed to fetch API keys.",
        error: error,
        status: error?.response?.status ?? 504,
      },
    };
  }
};

/**
 * Revoke an API key for the authenticated user
 * DELETE /me/api_keys/{key_id}
 */
export const revokeUserApiKey = async (
  keyId: number
): Promise<RevokeApiKeyResponse | any> => {
  try {
    const url = `${BASE_URL}/me/api_keys/${keyId}`;
    const response = await axios.delete(url);
    return response.data;
  } catch (error: any) {
    console.log("Error revoking user API key:", error);
    if (error.code === "ERR_CANCELED") return { canceled: true };

    return {
      error: {
        message: error?.response?.data?.details ?? "Failed to revoke API key.",
        error: error,
        status: error?.response?.status ?? 504,
      },
    };
  }
};
